import React, { useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import ImageUploader from "./ImageUploader";
import ImageSelector from "./ImageSelector";

const PostComposer = ({ existingPost = null, onSave, onCancel }) => {
    const [content, setContent] = useState(existingPost?.content || []);
    const [availableImages, setAvailableImages] = useState([]);
    const [showImageSelector, setShowImageSelector] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    const [metadata, setMetadata] = useState({
        title: existingPost?.title || "",
        author: existingPost?.author || "",
        scheduleDate: existingPost?.scheduleDate || "",
        expireDate: existingPost?.expireDate || "",
        isEnabled: existingPost?.isEnabled || false,
        isPosted: existingPost?.isPosted || false,
    });

    const addItem = (type) => {
        const newItem =
            type === "text"
                ? { type, content: "" }
                : type === "image"
                    ? { type, src: "", alt: "" }
                    : type === "quote"
                        ? { type, content: "" }
                        : type === "list"
                            ? { type, content: [""] } // Initialize with empty list
                            : { type, content: "" };
        setContent([...content, newItem]);
    };

    const updateContent = (index, updatedItem) => {
        const updatedContent = [...content];
        updatedContent[index] = updatedItem;
        setContent(updatedContent);
    };

    const removeContent = (index) => {
        setContent(content.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        const normalizedContent = content.map((item) =>
            item.type === "list"
                ? { ...item, items: item.content, content: undefined } // Convert content to items
                : item
        );

        // Generate slug and UUID for new posts (if required)
        const slug = existingPost?.slug || metadata.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
        const uuid = existingPost?.uuid || uuidv4();

        const post = { ...metadata, content: normalizedContent, slug, uuid };

        if (!post.author) {
            alert("Author is required.");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (existingPost._id) {
                // Update an existing post
                await axios.put(`/api/blog/${existingPost._id}`, post, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
            } else {
                // Create a new post
                await axios.post(`/api/blog/`, post, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
            }

            // Trigger the parent component's callback to refresh posts
            onSave();
        } catch (error) {
            // Extract error message
            const errorMessage =
                error.response?.data?.error || "Failed to save post. Please try again.";
            alert(errorMessage);
            console.error("Error saving post:", error);
        }
    };

    return (
        <div>
            <h2>{existingPost ? "Edit Blog Post" : "Create Blog Post"}</h2>
            <div className="metadata">
                <label>
                    Title
                    <input
                        type="text"
                        placeholder="Title"
                        value={metadata.title}
                        onChange={(e) => setMetadata({...metadata, title: e.target.value})}
                        className="input-title"
                    />
                </label>
                <label>
                    Author
                    <input
                        type="text"
                        placeholder="Author"
                        value={metadata.author}
                        onChange={(e) => setMetadata({...metadata, author: e.target.value})}
                        className="input-author"
                    />
                </label>
                <label>
                    Schedule Date
                    <input
                        type="datetime-local"
                        placeholder="Schedule Date"
                        value={
                            metadata.scheduleDate
                                ? new Date(metadata.scheduleDate).toISOString().slice(0, 16)
                                : ""
                        }
                        onChange={(e) =>
                            setMetadata({...metadata, scheduleDate: new Date(e.target.value).toISOString()})
                        }
                        className="input-datetime"
                    />
                </label>
                <label>
                    Expire Date
                    <input
                        type="datetime-local"
                        placeholder="Expire Date"
                        value={
                            metadata.expireDate
                                ? new Date(metadata.expireDate).toISOString().slice(0, 16)
                                : ""
                        }
                        onChange={(e) =>
                            setMetadata({...metadata, expireDate: new Date(e.target.value).toISOString()})
                        }
                        className="input-datetime"
                    />
                </label>
                <label className="table-row">
                    Enabled
                    <input
                        type="checkbox"
                        checked={metadata.isEnabled}
                        onChange={(e) =>
                            setMetadata({...metadata, isEnabled: e.target.checked})
                        }

                    />

                </label>
                <label className="table-row">
                    Posted
                    <input
                        type="checkbox"
                        checked={metadata.isPosted}
                        onChange={(e) =>
                            setMetadata({...metadata, isPosted: e.target.checked})
                        }
                    />
                </label>
            </div>
            <hr/>
            <div className="py-6">
            <h3>Content</h3>
            <div className="content-editor py-4">
                {content.map((item, index) => (
                    <div key={index} className="content-item">
                        <h4>Content Type: {item.type}</h4>
                        {item.type === "text" && (
                            <textarea
                                value={item.content}
                                onChange={(e) =>
                                    updateContent(index, {...item, content: e.target.value})
                                }
                                placeholder="Enter text content"
                                className="content-text"
                            />
                        )}
                        {item.type === "list" && (
                            <div className="list-editor">
                                {Array.isArray(item.content) ? (
                                    item.content.map((listItem, listIndex) => (
                                        <div key={listIndex} className="list-item flex items-center">
                                            <input
                                                type="text"
                                                value={listItem}
                                                onChange={(e) => {
                                                    const updatedList = [...item.content];
                                                    updatedList[listIndex] = e.target.value;
                                                    updateContent(index, {...item, content: updatedList});
                                                }}
                                                className="border px-2 py-1 mr-2 flex-1"
                                            />
                                            <button
                                                onClick={() => {
                                                    const updatedList = item.content.filter((_, i) => i !== listIndex);
                                                    updateContent(index, {...item, content: updatedList});
                                                }}
                                                className="bg-red-500 text-white px-2 py-1 rounded"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-red-500">Invalid list content</p>
                                )}
                                <button
                                    onClick={() => {
                                        const updatedList = [...(item.content || []), ""]; // Ensure content is an array
                                        updateContent(index, {...item, content: updatedList});
                                    }}
                                    className="bg-green-500 text-white px-2 py-1 mt-2 rounded"
                                >
                                    Add Item
                                </button>
                            </div>
                        )}

                        {item.type === "quote" && (
                            <textarea
                                value={item.content}
                                onChange={(e) =>
                                    updateContent(index, {...item, content: e.target.value})
                                }
                                placeholder="Enter quote content"
                                className="content-quote"
                            />
                        )}

                        {item.type === "image" && (
                            <div className="image-editor">
                                <h4>Upload New Image</h4>
                                <ImageUploader
                                    onUpload={(path) =>
                                        updateContent(index, { ...item, src: path })
                                    }
                                />

                                <h4>Select Existing Image</h4>
                                <ImageSelector
                                    onSelect={(path) =>
                                        updateContent(index, { ...item, src: path })
                                    }
                                />

                                <input
                                    type="text"
                                    placeholder="Alt Text"
                                    value={item.alt}
                                    onChange={(e) =>
                                        updateContent(index, { ...item, alt: e.target.value })
                                    }
                                    className="content-image-alt border rounded px-4 py-2 w-full mt-4"
                                />

                                {item.src && (
                                    <div className="mt-4">
                                        <h4>Image Preview:</h4>
                                        <img
                                            src={item.src}
                                            alt={item.alt || "Selected Image"}
                                            className="rounded shadow"
                                        />
                                    </div>
                                )}
                            </div>
                        )}


                        <button
                            onClick={() => removeContent(index)}
                            className="remove-content"
                        >
                            Remove
                        </button>
                    </div>
                ))}
                <div className="add-content-buttons py-6">
                    <button onClick={() => addItem("text")} className="add-text m-2">
                        Add Text
                    </button>
                    <button onClick={() => addItem("image")} className="add-image m-2">
                        Add Image
                    </button>
                    <button onClick={() => addItem("list")} className="add-list m-2">
                        Add List
                    </button>
                    <button onClick={() => addItem("quote")} className="add-quote m-2">
                        Add Quote
                    </button>
                </div>
            </div>
            <hr/>
                <div className="py-6">
                    <button onClick={handleSave} className="save-post">
                        Save Post
                    </button>
                    <button
                        onClick={onCancel}
                        className="bg-gray-500 text-white px-4 py-2 rounded ml-4"
                    >
                        Cancel
                    </button>
                </div>
                <hr/>

                <div className="py-6">
                    <h3>Live Preview</h3>
                    <div className="py-6">
                        <div className="post-preview">
                        <h1>{metadata.title}</h1>
                        <p><em>By {metadata.author}</em></p>

                        <div>
                            {content.map((item, index) => (
                                <div key={index}>
                                    {item.type === "text" && <p>{item.content}</p>}
                                    {item.type === "image" && (
                                        <div>
                                            <img
                                                src={item.src}
                                                alt={item.alt}
                                                style={{maxWidth: "100%"}}
                                            />
                                            <p>
                                                <em>{item.alt}</em>
                                            </p>
                                        </div>
                                    )}
                                    {item.type === "list" && (
                                        <ul key={index} className="list-disc pl-5 my-4">
                                            {Array.isArray(item.content) &&
                                                item.content.map((item, listIndex) => (
                                                    <li key={listIndex}>{item}</li>
                                                ))}
                                        </ul>
                                    )}
                                    {item.type === "quote" && (
                                        <blockquote className="italic text-gray-600 my-4">
                                            {item.content}
                                        </blockquote>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p>
                            <strong>Scheduled:</strong>{" "}
                            {metadata.scheduleDate
                                ? new Date(metadata.scheduleDate).toLocaleString()
                                : "Not scheduled"}
                        </p>
                        <p>
                            <strong>Expires:</strong>{" "}
                            {metadata.expireDate
                                ? new Date(metadata.expireDate).toLocaleString()
                                : "No expiration"}
                        </p>
                        <p>
                            <strong>Status:</strong> {metadata.isEnabled ? "Enabled" : "Disabled"} |{" "}
                            {metadata.isPosted ? "Posted" : "Not Posted"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default PostComposer;
